import {
  Flex,
  FormControl,
  FormLabel,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from "@chakra-ui/react";
import React, { useState } from "react";
import AppButton from "@/features/line-sheet-sets/app-button";
import AppInput from "@/features/ui/app-input";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import ToBeUpdatedOrderSheetList, {
  ToBeUpdatedOrderSheet,
} from "@/features/order-status/modals/to-be-updated-order-sheet-list";
import ChangeOrderSheetStatusConfirmMessage from "@/features/order-status/change-order-sheet-status-confirm-message";
import useRegisterBoutiqueShipment, {
  RegisterBoutiqueShipmentResponse,
} from "@/features/order-status/hooks/use-register-boutique-shipment";

export interface BoutiqueShipOrderSheetSetModalProps {
  boutiqueId: number;
  orderSheetSetId: number;
  toBeUpdateOrderSheets: ToBeUpdatedOrderSheet[];
  onConfirm: (response: RegisterBoutiqueShipmentResponse) => void;
  isOpen: boolean;
  onClose: () => void;
}

// 부티크가 오더를 운송 중으로 바꾸는 모달 — 배송사·송장번호(국내 배송)를 받아 운송/통관 건을 만들고 상태를 바꾼다.
// 값은 운송의 국내배송사·송장번호 칸에 들어간다.
// (에이전시는 운송/통관 탭에서 전체 양식으로 운송을 등록하고, 그 등록이 오더를 운송 중으로 바꾼다)
export default function BoutiqueShipOrderSheetSetModal({
  boutiqueId,
  orderSheetSetId,
  toBeUpdateOrderSheets,
  onConfirm,
  isOpen,
  onClose,
}: BoutiqueShipOrderSheetSetModalProps) {
  const { t, tTitle } = useI18nHelper();
  const { error: showError, warning: showWarning } = useAppToasts();
  const [carrierName, setCarrierName] = useState("");
  const [trackingNumber, setTrackingNumber] = useState("");
  const { fire, isLoading } = useRegisterBoutiqueShipment();

  const handleSubmit = async () => {
    const carrier = carrierName.trim();
    const tracking = trackingNumber.trim();
    if (!carrier) {
      showWarning(
        t("boutique_ship_modal.required", {
          ns: "order_sheet_sets",
          field: tTitle("domestic_carrier_name"),
        })
      );
      return;
    }
    if (!tracking) {
      showWarning(
        t("boutique_ship_modal.required", {
          ns: "order_sheet_sets",
          field: tTitle("domestic_tracking_number"),
        })
      );
      return;
    }
    try {
      const response = await fire(
        { boutiqueId, orderSheetSetId },
        { carrierName: carrier, trackingNumber: tracking },
        { rethrow: true }
      );
      if (response) {
        onConfirm(response);
        onClose();
      }
    } catch (e) {
      showError(e);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={"2xl"}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize={"16px"} fontWeight={"700"}>
          {tTitle("change_status")}
        </ModalHeader>
        <ModalBody>
          <Flex flexDirection={"column"} gap={"16px"} fontSize={"12px"}>
            <ToBeUpdatedOrderSheetList
              header={
                <ChangeOrderSheetStatusConfirmMessage
                  status={"TRANSPORTATION_ING"}
                />
              }
              orderSheets={toBeUpdateOrderSheets}
            />
            <Text color={"#6F6F6C"}>
              {t("boutique_ship_modal.description", { ns: "order_sheet_sets" })}
            </Text>
            <Flex gap={"12px"}>
              <FormControl isRequired={true}>
                <FormLabel fontSize={"12px"} fontWeight={"700"} margin={0}>
                  {tTitle("domestic_carrier_name")}
                </FormLabel>
                <AppInput
                  data-testid={"boutique_ship_carrier_name"}
                  value={carrierName}
                  placeholder={t("boutique_ship_modal.carrier_name_placeholder", {
                    ns: "order_sheet_sets",
                  })}
                  onChange={(value) => setCarrierName(value ?? "")}
                />
              </FormControl>
              <FormControl isRequired={true}>
                <FormLabel fontSize={"12px"} fontWeight={"700"} margin={0}>
                  {tTitle("domestic_tracking_number")}
                </FormLabel>
                <AppInput
                  data-testid={"boutique_ship_tracking_number"}
                  value={trackingNumber}
                  placeholder={t("boutique_ship_modal.tracking_number_placeholder", {
                    ns: "order_sheet_sets",
                  })}
                  onChange={(value) => setTrackingNumber(value ?? "")}
                />
              </FormControl>
            </Flex>
          </Flex>
        </ModalBody>
        <ModalFooter display={"flex"} gap={"12px"}>
          <AppButton onClick={onClose} isDisabled={isLoading}>
            {tTitle("not_now")}
          </AppButton>
          <AppButton
            variant={"primary"}
            isLoading={isLoading}
            onClick={handleSubmit}
          >
            {tTitle("change")}
          </AppButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
